import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SecurityContext, SimpleChanges } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { BaseFormDirective, FileCapture } from "@modules/common/components";
import { BaseAttachmentDto } from "@modules/models";
import { AnswerFile, ChecklistService, JobService, newChecklistEntry, SnackBarService, WebLayoutService } from "@services";
import { ChecklistDto, ChecklistEntryDto, ChecklistQuestionDto } from "@shared/models/checklist/ChecklistDto";
import { Observable } from "rxjs";

@Component({
  selector: "abi-job-checklist",
  templateUrl: "./checklist.component.html",
  styleUrls: ["./checklist.component.scss"],
})
export class ChecklistComponent extends BaseFormDirective implements OnInit, OnChanges {
  // @Input() jobId: string;
  @Input() checklistEntry: ChecklistEntryDto; // and answers
  @Input() getChecklistDto: () => Observable<ChecklistDto>;
  @Input() updateResponse: (payload: ChecklistEntryDto) => Observable<boolean>;
  @Input() uploadFileToServer: (index: number, fileCap: FileCapture) => Observable<BaseAttachmentDto>;
  @Output() viewAttachment: EventEmitter<string> = new EventEmitter();
  @Output() attachmentUploaded: EventEmitter<BaseAttachmentDto> = new EventEmitter();
  checklistData: ChecklistDto;
  checklistAnswers: ChecklistEntryDto;
  public currentQuestion: string;
  public busyUploading = false;
  public busyLoading = true;

  get closed(): boolean {
    return this.checklistEntry?.closed === 1 || false;
  }

  get answers(): FormArray {
    return this.form?.get("answers") as FormArray;
  }

  constructor(
    protected layoutService: WebLayoutService,
    // private checklistService: ChecklistService,
    formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
    private jobService: JobService,
    private snackBar: SnackBarService,
    ) {
    super(layoutService, formBuilder);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if(changes.checklistEntry) {
      this.checklistAnswers = changes.checklistEntry.currentValue;
      this.loadData();
    }
  }

  // @TODO: check behaviour on Job/Checklist for Unlocks
  public unlockChecklist() {
    const answerPayload = this.gatherAnswersPayload();// reset to colsed=0
    // this.checklistService.updateResponse(this.jobId, answerPayload).subscribe(res => {
    this.updateResponse(answerPayload).subscribe(res => {
      this.form.markAsPristine();
      this.answers.controls?.[0]?.enable();
      this.currentQuestion = this.checklistAnswers.answers[0]?.questionId;
    });
  }

  createForm() {
    this.form = this.formBuilder.group({
      checklistId: "",
      answers: this.formBuilder.array([]),
    });
  }

  ngOnInit(): void {
    super.ngOnInit(); // create form
    this.checklistAnswers = this.checklistEntry || newChecklistEntry();
    // console.log('checklist answers', this.checklistAnswers);
    this.loadData();
  }

  public isBusyLoading() {
    return this.busyLoading;
  }

  loadData() {
    this.busyLoading = true;
    this.getChecklistDto().subscribe((checklist) => {
      // console.log('Load data, checklist', checklist);
    // this.checklistService.getJobChecklist(this.jobId) // TODO: checklist Id here
    // .subscribe((checklist) => {
      if(checklist) {
        this.displayChecklist(checklist);
        this.doStart();
      }
      this.busyLoading = false;
    });
  }

  displayChecklist(checklist: ChecklistDto) {
    this.checklistData = checklist;
    const answers = checklist.questions.map((q) => this.generateFormGroupQuestion(q));
    if(!this.answers) return;
    this.answers.clear();
    for (const answer of answers) {
      this.answers.push(answer);
    }
    this.form.get("checklistId").patchValue(checklist.code);// dto uses 'code'...
  }

  generateFormGroupQuestion(question: ChecklistQuestionDto) {
    const resp = this.checklistAnswers.answers.find(
      (a) => a.questionId === question.questionId
    );
    const group = this.formBuilder.group({
      questionId: question.questionId,
      answer: [resp?.answer, Validators.required],
      notes: resp?.notes,
    });

    if (question.questionId !== this.currentQuestion) group.disable();
    return group;
  }

  safeAnswer(text: string): SafeHtml {
    return this.sanitizer.sanitize(SecurityContext.HTML, text);
  }

  // Some Checklist items should only be available in a specific job status
  // note: we also want to filter available answers based on the current job status: so each answer will have a 'enableStatus' [LOG,etc...]
  public updateAnswer(questionId: number, answer: string) {
    // console.log('update answer called');
    const question = this.checklistData.questions[questionId];
    // if (Object.keys(question.statuses).length)
    //   this.answerStatus = question.statuses[answer] ?? "";

    // conversely, some answers will enable certain job statuses
    // we can use the 'statuses' here for:
    // an answer key is used and lists possible job statuses

    this.answers.at(questionId).patchValue({ answer });

    if(!question.dependancies) return;
    for (const dep of question.dependancies) {
      // if the current answer is set in answerFrom - we skip that questionId
      const ans = answer === dep.answerFrom ? "SKIPPED" : "";
      const idx = this.checklistData.questions.findIndex(
        (q) => q.questionId === dep.dependancyId
        );
      // console.log(ans, idx)
      this.answers.at(idx).patchValue({ answer: ans });
    }
    // if (loc === "end")
    //   this.complete();
    // this.saveChanges();
  }

  public selectQuestion(direction: number): string {
    let curId = this.checklistData.questions.findIndex(
      (q) => q.questionId === this.currentQuestion
    );
    if (curId >= 0)
      this.answers.at(curId).disable();

    curId = curId + direction;
    if (curId >= 0 && curId < this.answers.length) {
      while (direction && (this.answers.at(curId) as FormGroup).getRawValue().answer === "SKIPPED") {
        // going backwards, clears previously skipped answers
        if(direction === -1) (this.answers.at(curId) as FormGroup).patchValue({ answer: null });
        curId = curId + direction;
      }
      this.currentQuestion = this.checklistData.questions[curId].questionId;
      this.answers.at(curId).enable();
      return "";
    } else if(curId <= 0) {
      curId = 0;
    }
    return curId === 0 ? "start" : "end";
  }

  private doStart() {
    this.currentQuestion = "";
    if(this.closed)
      return this.closeForm();
    // else
    //   this.unlockForm();

    for (let i  = 0; i < this.checklistAnswers.answers.length; i++) {
      const ans = this.checklistAnswers.answers[i];
      if (this.validateAnswer(i, false)) this.currentQuestion = ans.questionId;
      else break;
    }
    console.log('current question', this.currentQuestion);
    // for (const ans of this.checklistAnswers?.answers) {
    //   if (!!ans.answer) this.currentQuestion = ans.questionId;
    //   else break;
    // }
    this.selectQuestion(+1);
  }

  toggleAnswer(index: number, value: string){
    const control = this.answers.at(index) as FormGroup;
    const currentValue = control.getRawValue();
    if(currentValue.answer !== value)
      this.updateAnswer(index, value);
    else
      this.updateAnswer(index, null);
  }

  updateFileAnswer(index: number, aFile: AnswerFile) {
    this.answers.at(index).patchValue({ answer: aFile });
  }

  clearAnswer(index) {
    this.updateAnswer(index, null);
    this.selectQuestion(-1);
  }

  saveAndNext(i: number) {
    //validate first
    if(!this.validateAnswer(i))
      return;
    this.form.markAsPending();
    const answerPayload = this.gatherAnswersPayload();
    this.updateResponse(answerPayload)
    .subscribe(success => {
      if(success) {
        this.form.markAsPristine();
        this.selectQuestion(+1);
      } else {
        this.answers.at(i).enable();
        this.form.markAsDirty();
        console.log('Checklist: error saving answer', answerPayload);
      }
    });
  }

  validateAnswer(i: number, triggerAlert = true): boolean {
    const controlValue = this.answers.controls[i]?.value;
    const controlQuestionDataType = this.checklistData.questions[i].dataType;
    const controlQuestionTypeId = this.checklistData.questions[i].typeId;
    // console.log('controlValue', controlValue);
    // console.log('controlQuestionDataType', controlQuestionDataType);
    switch(controlQuestionTypeId) {
      case "NUMBER":
        console.log('controlValue', controlValue);
        if(typeof(controlValue.answer) !== "number") {
          if(triggerAlert)
            this.snackBar.warning('Please enter a valid number');
          return false;
        }
        break;

        default:
          if(!controlValue.answer) {
            // this.snackBar.warning('Please enter a valid string');
          if(triggerAlert)
            this.snackBar.warning('Please check that you have answered before saving.');
          return false;
        }
        break;
    }

    return true;
  }

  /**
   * Mark checklist as closed
   * @param i current question to disable when finished
   */
  complete(i: number) {
    if(!this.validateAnswer(i))
    return;
    this.form.markAsPending();
    const answerPayload = this.gatherAnswersPayload();
    answerPayload.closed = 1;
    this.updateResponse(answerPayload).subscribe(res => {
    // this.checklistService.updateResponse(this.jobId, answerPayload).subscribe(res => {
      this.form.markAsPristine();
      this.currentQuestion = null;
      this.closeForm();
    });
  }

  closeForm() {
    this.currentQuestion = null;
    this.answers.disable();
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  unlockForm() {
    this.answers.enable();
  }

  gatherAnswersPayload() {
    const formData = this.form.value;// note disabled groups dont give data
    const answersArray = this.answers.controls.map(c => c.value).filter((a, i) => this.validateAnswer(i, false));// grab all values - filter non answers
    const payload = newChecklistEntry();
    payload.answers = answersArray;
    payload.checklistId = formData.checklistId;
    return payload;
  }

  canGoBack(questionId: number) {
    const currentQuestionIndex = this.checklistData.questions.findIndex(
      (q) => q.questionId === this.currentQuestion
    );
    return questionId === currentQuestionIndex && currentQuestionIndex > 0;
  }
  // need to sort this out...?
  isPreviousQuestion(questionId: number) {
    const currentQuestionIndex = this.checklistData.questions.findIndex(
      (q) => q.questionId === this.currentQuestion
    );
    // check between current quyestion and this index
    let indexIteration = currentQuestionIndex - 1;
    while(indexIteration > -1 && (this.answers.at(indexIteration) as FormGroup).getRawValue().answer === "SKIPPED") {
      indexIteration -= 1;
    }
    if(indexIteration === questionId) return true;
    // console.log('isPrevisquestion - index iteration', indexIteration)
    // console.log('questionId', questionId)
    // console.log('indexitation', indexIteration)
    return false;
  }

  uploadFile(index: number, fileCap: FileCapture) {
    this.busyUploading = true;
    this.uploadFileToServer(index, fileCap)
    .subscribe((result: BaseAttachmentDto) => {
    // this.jobService.uploadAttachment(this.jobId, fileCap.file, fileCap.description, (prog) => {})
    // .subscribe((result: BaseAttachmentDto) => {
      if (result) {
        this.busyUploading = false;
        this.updateAnswer(index, result.attachmentId);
        this.onAttachmentUploaded(result);
      }
    });
  }

  selectRadio(index, value) {
    this.updateAnswer(index, value);
  }

  answerGood(index: number) {
    if((this.answers.controls[index] as FormGroup).getRawValue().answer) {
      return true;
    }
    return false;
  }

  saveChanges() {
    const answerData = this.answers.getRawValue() as any[];
    const answerArray = answerData.map((d) => ({
      questionId: d.questionId,
      answer: d.answer,
      notes: d.notes,
    }));
    const checklistResponsePayload = newChecklistEntry();
    checklistResponsePayload.answers = answerArray;
    this.updateResponse(checklistResponsePayload).subscribe((x) => {
    // this.checklistService.updateResponse(this.jobId, checklistResponsePayload).subscribe((x) => {
      // console.log(x);
    });
  }

  isLast(i: number){
    return this.checklistData.questions[i].questionId === this.checklistData.questions[this.checklistData.questions.length-1].questionId;
  }

  onViewAttachment(attachmentId: string) {
    this.viewAttachment.emit(attachmentId);
  }

  onAttachmentUploaded(attachment: BaseAttachmentDto) {
    this.attachmentUploaded.emit(attachment);
  }

  // Proposed Features
  // 1. ad-hoc answering of questions
  // 2. question dependencies
}
